import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Search, Save, X, Trash2, Plus, RotateCcw, Edit3, Upload, Layers, AlertTriangle } from "lucide-react";

const DEFAULT_CATS = ["Federal Law", "SAFE Act", "Loan Products", "MLO Activities", "Ethics & Fraud"];

export default function AdminFlashcardsEditor() {
  const [cards, setCards] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATS);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // all | edited | custom
  const [editingNum, setEditingNum] = useState(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [page, setPage] = useState(1);
  const perPage = 50;

  async function load() {
    setLoading(true);
    const { data } = await api.get("/admin/flashcards");
    setCards(data.cards);
    setCategories([...new Set([...DEFAULT_CATS, ...data.categories])]);
    setLoading(false);
  }

  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [query, catFilter, statusFilter]);

  const filtered = useMemo(() => cards.filter((c) => {
    if (catFilter !== "all" && c.category !== catFilter) return false;
    if (statusFilter === "edited" && !c._edited) return false;
    if (statusFilter === "custom" && !c._custom) return false;
    if (query) {
      const s = query.toLowerCase();
      if (!c.front.toLowerCase().includes(s) && !c.back.toLowerCase().includes(s) && !String(c.number).includes(s)) return false;
    }
    return true;
  }), [cards, query, catFilter, statusFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / perPage));
  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  const stats = useMemo(() => ({
    total: cards.length,
    edited: cards.filter(c => c._edited).length,
    custom: cards.filter(c => c._custom).length,
  }), [cards]);

  return (
    <div data-testid="admin-flashcards-editor">
      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <StatChip n={stats.total} l="Total cards" />
        <StatChip n={stats.edited} l="Edited" tone="amber" />
        <StatChip n={stats.custom} l="Custom (admin-added)" tone="emerald" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" strokeWidth={1.5} />
          <input
            type="text"
            placeholder="Search front, back, or card #…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="fc-search"
            className="w-full pl-9 pr-3 py-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm focus:outline-none focus:border-[var(--dw-navy)]"
          />
        </div>
        <select
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
          data-testid="fc-cat-filter"
          className="text-sm border border-[var(--dw-navy)]/15 rounded-sm px-3 py-2"
        >
          <option value="all">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="fc-status-filter"
          className="text-sm border border-[var(--dw-navy)]/15 rounded-sm px-3 py-2"
        >
          <option value="all">All cards</option>
          <option value="edited">Edited</option>
          <option value="custom">Custom only</option>
        </select>
        <button
          onClick={() => setBulkOpen(true)}
          data-testid="fc-bulk-btn"
          className="dw-btn-outline rounded-sm px-4 py-2 text-sm inline-flex items-center gap-1.5"
        >
          <Upload className="w-4 h-4" strokeWidth={1.5} /> Bulk import
        </button>
        <button
          onClick={() => setCreatingNew(true)}
          data-testid="fc-new-btn"
          className="dw-btn-gold rounded-sm px-4 py-2 text-sm inline-flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" strokeWidth={1.5} /> Add card
        </button>
      </div>

      {loading ? (
        <div className="text-neutral-500 py-10 text-center">Loading flashcards…</div>
      ) : (
        <>
          <div className="text-xs text-neutral-500 mb-3">
            Showing {paged.length} of {filtered.length} matches · Page {page} of {pageCount}
          </div>
          <div className="space-y-1.5">
            {paged.length === 0 && (
              <div className="text-neutral-500 py-10 text-center text-sm">No cards match your filters.</div>
            )}
            {paged.map((c) => (
              <FlashcardRow
                key={c.number}
                card={c}
                editing={editingNum === c.number}
                categories={categories}
                onEdit={() => setEditingNum(c.number)}
                onClose={() => setEditingNum(null)}
                onReload={load}
              />
            ))}
          </div>
          {pageCount > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6" data-testid="fc-pagination">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="text-sm px-3 py-1.5 border border-[var(--dw-navy)]/15 rounded-sm disabled:opacity-40">Previous</button>
              <span className="text-sm text-neutral-600 px-3">Page {page} / {pageCount}</span>
              <button onClick={() => setPage(Math.min(pageCount, page + 1))} disabled={page === pageCount} className="text-sm px-3 py-1.5 border border-[var(--dw-navy)]/15 rounded-sm disabled:opacity-40">Next</button>
            </div>
          )}
        </>
      )}

      {creatingNew && (
        <NewCardModal categories={categories} onClose={() => setCreatingNew(false)} onCreated={() => { setCreatingNew(false); load(); }} />
      )}
      {bulkOpen && (
        <BulkImportModal categories={categories} onClose={() => setBulkOpen(false)} onImported={() => { setBulkOpen(false); load(); }} />
      )}
    </div>
  );
}

function StatChip({ n, l, tone }) {
  const cls = {
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
  }[tone] || "border-[var(--dw-navy)]/12 bg-white text-[var(--dw-navy)]";
  return (
    <div className={`border rounded-sm px-4 py-3 ${cls}`}>
      <div className="font-serif text-2xl leading-none">{n}</div>
      <div className="text-[10px] uppercase tracking-widest mt-1 opacity-80">{l}</div>
    </div>
  );
}

function FlashcardRow({ card, editing, categories, onEdit, onClose, onReload }) {
  const [form, setForm] = useState({ front: card.front, back: card.back, category: card.category });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (editing) setForm({ front: card.front, back: card.back, category: card.category }); }, [editing, card]);

  async function save() {
    setSaving(true);
    try {
      await api.put(`/admin/flashcards/${card.number}`, form);
      onReload();
      onClose();
    } finally { setSaving(false); }
  }

  async function del() {
    if (!window.confirm(`Delete card #${card.number}? ${card._custom ? "It's a custom card and will be gone permanently." : "It will be hidden from students. Reset to bring back."}`)) return;
    await api.delete(`/admin/flashcards/${card.number}`);
    onReload();
  }

  async function reset() {
    if (!window.confirm(`Reset card #${card.number} to the original seed content?`)) return;
    await api.post(`/admin/flashcards/${card.number}/reset`);
    onReload();
  }

  if (!editing) {
    return (
      <div className="border border-[var(--dw-navy)]/12 rounded-sm p-3 bg-white" data-testid={`fc-row-${card.number}`}>
        <div className="flex items-start gap-3">
          <div className="text-xs font-mono-plex text-neutral-500 w-12 shrink-0 pt-1">#{card.number}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-[10px] px-1.5 py-0.5 border border-[var(--dw-navy)]/15 rounded-sm text-neutral-600">{card.category}</span>
              {card._edited && <span className="text-[10px] px-1.5 py-0.5 border border-amber-400 bg-amber-50 text-amber-800 rounded-sm inline-flex items-center gap-1"><Edit3 className="w-2.5 h-2.5" /> Edited</span>}
              {card._custom && <span className="text-[10px] px-1.5 py-0.5 border border-emerald-400 bg-emerald-50 text-emerald-800 rounded-sm">Custom</span>}
            </div>
            <div className="text-sm text-[var(--dw-navy)] font-medium leading-snug mb-0.5 truncate">{card.front}</div>
            <div className="text-xs text-neutral-600 leading-snug truncate">→ {card.back}</div>
          </div>
          <div className="flex gap-1 shrink-0">
            {card._edited && (
              <button onClick={reset} title="Reset" data-testid={`fc-reset-${card.number}`} className="w-8 h-8 flex items-center justify-center border border-[var(--dw-navy)]/12 hover:border-amber-500 rounded-sm text-neutral-500 hover:text-amber-600">
                <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            )}
            <button onClick={onEdit} data-testid={`fc-edit-${card.number}`} className="w-8 h-8 flex items-center justify-center border border-[var(--dw-navy)]/12 hover:border-[var(--dw-navy)] rounded-sm text-neutral-500 hover:text-[var(--dw-navy)]">
              <Edit3 className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
            <button onClick={del} data-testid={`fc-del-${card.number}`} className="w-8 h-8 flex items-center justify-center border border-[var(--dw-navy)]/12 hover:border-red-500 rounded-sm text-neutral-500 hover:text-red-600">
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-2 border-[var(--dw-gold)] rounded-sm p-4 bg-[var(--dw-parchment)]" data-testid={`fc-editform-${card.number}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-serif text-lg text-[var(--dw-navy)]">Editing card #{card.number}</div>
        <button onClick={onClose}><X className="w-4 h-4 text-neutral-500 hover:text-[var(--dw-navy)]" strokeWidth={1.5} /></button>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Front (question / term)</label>
          <textarea value={form.front} onChange={(e) => setForm({ ...form, front: e.target.value })} rows={2} data-testid="fc-field-front" className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Back (answer / definition)</label>
          <textarea value={form.back} onChange={(e) => setForm({ ...form, back: e.target.value })} rows={2} data-testid="fc-field-back" className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Category</label>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="fc-field-category" className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm">
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="text-sm text-neutral-600 hover:text-[var(--dw-navy)] px-4 py-2">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="fc-save-btn" className="dw-btn-gold rounded-sm px-5 py-2 text-sm inline-flex items-center gap-1.5 disabled:opacity-60">
          <Save className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

function NewCardModal({ categories, onClose, onCreated }) {
  const [form, setForm] = useState({ front: "", back: "", category: categories[0] || "Federal Law" });
  const [saving, setSaving] = useState(false);

  async function create() {
    if (!form.front || !form.back) { alert("Front and back are required."); return; }
    setSaving(true);
    try {
      await api.post("/admin/flashcards", form);
      onCreated();
    } catch (e) {
      alert("Failed: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="fc-new-modal">
      <div className="max-w-lg w-full bg-[var(--dw-parchment)] border border-[var(--dw-navy)]/20 rounded-sm shadow-2xl">
        <div className="flex items-center justify-between border-b border-[var(--dw-navy)]/10 px-5 py-3">
          <div className="font-serif text-xl text-[var(--dw-navy)]">New flashcard</div>
          <button onClick={onClose}><X className="w-4 h-4 text-neutral-500 hover:text-[var(--dw-navy)]" strokeWidth={1.5} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Front (question / term)</label>
            <textarea value={form.front} onChange={(e) => setForm({ ...form, front: e.target.value })} rows={2} data-testid="fc-new-front" className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Back (answer / definition)</label>
            <textarea value={form.back} onChange={(e) => setForm({ ...form, back: e.target.value })} rows={2} data-testid="fc-new-back" className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="fc-new-cat" className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm">
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-[var(--dw-navy)]/10 px-5 py-3">
          <button onClick={onClose} className="text-sm text-neutral-600 hover:text-[var(--dw-navy)] px-4 py-2">Cancel</button>
          <button onClick={create} disabled={saving} data-testid="fc-new-create" className="dw-btn-gold rounded-sm px-5 py-2 text-sm inline-flex items-center gap-1.5 disabled:opacity-60">
            <Save className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Creating…" : "Create card"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkImportModal({ categories, onClose, onImported }) {
  const [format, setFormat] = useState("pipe"); // pipe | tab | json
  const [text, setText] = useState("");
  const [defaultCategory, setDefaultCategory] = useState(categories[0] || "Federal Law");
  const [replaceCategory, setReplaceCategory] = useState(false);
  const [parsed, setParsed] = useState({ cards: [], errors: [] });
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    setParsed(parse(text, format, defaultCategory));
  }, [text, format, defaultCategory]);

  function parse(text, fmt, defaultCat) {
    const cards = [];
    const errors = [];
    if (!text.trim()) return { cards, errors };
    if (fmt === "json") {
      try {
        const data = JSON.parse(text);
        const list = Array.isArray(data) ? data : [];
        list.forEach((row, i) => {
          const front = row.front || row.f || row.question;
          const back = row.back || row.b || row.answer;
          const category = row.category || row.cat || defaultCat;
          if (!front || !back) { errors.push(`Row ${i + 1}: missing front/back`); return; }
          cards.push({ front: String(front).trim(), back: String(back).trim(), category: String(category).trim() });
        });
      } catch (e) {
        errors.push("Invalid JSON: " + e.message);
      }
    } else {
      const sep = fmt === "tab" ? /\t/ : /\s*\|\s*/;
      text.split(/\r?\n/).forEach((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return;
        const parts = trimmed.split(sep);
        if (parts.length < 2) { errors.push(`Line ${i + 1}: needs at least 2 columns`); return; }
        const [front, back, category] = parts;
        cards.push({ front: front.trim(), back: back.trim(), category: (category || defaultCat).trim() });
      });
    }
    return { cards, errors };
  }

  async function doImport() {
    if (parsed.cards.length === 0) return;
    if (parsed.errors.length > 0 && !window.confirm(`${parsed.errors.length} lines had errors and will be skipped. Continue?`)) return;
    setImporting(true);
    try {
      const payload = { cards: parsed.cards };
      if (replaceCategory) payload.replace_category = defaultCategory;
      const { data } = await api.post("/admin/flashcards/bulk-import", payload);
      alert(`Imported ${data.inserted} cards.`);
      onImported();
    } catch (e) {
      alert("Import failed: " + (e?.response?.data?.detail || e.message));
    } finally { setImporting(false); }
  }

  const samplePipe = "What law prohibits kickbacks? | RESPA | Federal Law\nWhat does APR stand for? | Annual Percentage Rate | Federal Law";
  const sampleTab = "Front\tBack\tCategory";
  const sampleJson = '[{"front":"...","back":"...","category":"Federal Law"}]';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="fc-bulk-modal">
      <div className="max-w-3xl w-full max-h-[90vh] bg-[var(--dw-parchment)] border border-[var(--dw-navy)]/20 rounded-sm shadow-2xl flex flex-col">
        <div className="flex items-center justify-between border-b border-[var(--dw-navy)]/10 px-5 py-3">
          <div className="flex items-center gap-2">
            <Upload className="w-4 h-4 text-[var(--dw-gold-soft)]" strokeWidth={1.5} />
            <div className="font-serif text-xl text-[var(--dw-navy)]">Bulk import flashcards</div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-neutral-500 hover:text-[var(--dw-navy)]" strokeWidth={1.5} /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Format</label>
              <select value={format} onChange={(e) => setFormat(e.target.value)} data-testid="fc-bulk-format" className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm">
                <option value="pipe">Pipe-separated (front | back | category)</option>
                <option value="tab">Tab-separated (Excel/Sheets)</option>
                <option value="json">JSON array</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Default category (if omitted)</label>
              <select value={defaultCategory} onChange={(e) => setDefaultCategory(e.target.value)} data-testid="fc-bulk-default-cat" className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm">
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <label className="text-xs flex items-center gap-2 text-neutral-700 pb-2">
                <input type="checkbox" checked={replaceCategory} onChange={(e) => setReplaceCategory(e.target.checked)} data-testid="fc-bulk-replace" />
                Replace all custom cards in {defaultCategory}
              </label>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Paste cards</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={12}
              placeholder={format === "json" ? sampleJson : format === "tab" ? sampleTab : samplePipe}
              data-testid="fc-bulk-text"
              className="w-full p-3 text-sm border border-[var(--dw-navy)]/15 rounded-sm font-mono-plex leading-relaxed"
            />
            <div className="text-[11px] text-neutral-500 mt-1">
              {format === "pipe" && "One card per line. Columns separated by |. Third column (category) is optional."}
              {format === "tab" && "Paste directly from Excel or Google Sheets."}
              {format === "json" && "Array of objects with front, back, category fields."}
            </div>
          </div>

          {(parsed.cards.length > 0 || parsed.errors.length > 0) && (
            <div className="border border-[var(--dw-navy)]/12 rounded-sm p-3 bg-white/60">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-[var(--dw-navy)]">Parsed preview</div>
                <div className="text-xs text-neutral-600 space-x-3">
                  <span className="text-emerald-700">{parsed.cards.length} valid</span>
                  {parsed.errors.length > 0 && <span className="text-red-700 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {parsed.errors.length} errors</span>}
                </div>
              </div>
              <div className="max-h-40 overflow-y-auto space-y-1 text-xs">
                {parsed.cards.slice(0, 20).map((c, i) => (
                  <div key={i} className="flex gap-2 border-b border-neutral-100 py-1">
                    <span className="text-neutral-400 w-6 shrink-0">{i + 1}.</span>
                    <span className="text-[var(--dw-navy)] flex-1 truncate">{c.front}</span>
                    <span className="text-neutral-500 flex-1 truncate">→ {c.back}</span>
                    <span className="text-[10px] px-1.5 py-0.5 border border-[var(--dw-navy)]/15 rounded-sm text-neutral-600 shrink-0">{c.category}</span>
                  </div>
                ))}
                {parsed.cards.length > 20 && <div className="text-neutral-500">…and {parsed.cards.length - 20} more</div>}
                {parsed.errors.map((e, i) => <div key={"e" + i} className="text-red-700 pt-1">⚠ {e}</div>)}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-[var(--dw-navy)]/10 px-5 py-3">
          <button onClick={onClose} className="text-sm text-neutral-600 hover:text-[var(--dw-navy)] px-4 py-2">Cancel</button>
          <button onClick={doImport} disabled={importing || parsed.cards.length === 0} data-testid="fc-bulk-import" className="dw-btn-gold rounded-sm px-5 py-2 text-sm inline-flex items-center gap-1.5 disabled:opacity-60">
            <Upload className="w-4 h-4" strokeWidth={1.5} /> {importing ? "Importing…" : `Import ${parsed.cards.length} card${parsed.cards.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
